/**
 * Map of string code to a localized text
 */
export type TranslationsMap = Record<string, any>; // TODO Record<string, TranslationsMap> when typescript 4.1 release

export interface PlainString {
	type: 'plain';
	value: string;
}

export interface InterpolationInjectPart {
	label: string;
	content?: string;
}

export interface InterpolationString {
	type: 'interpolation';
	parts: (string | InterpolationInjectPart)[];
}

/**
 * Map of string code to a localized text
 */
export type TranslationsFlatMap = Record<string, PlainString | InterpolationString>;

const interpolateRegExp = /\{(\w+?)(:(.+?))?\}/g;

/**
 * Parse the interlaced string into an array of plaintext parts and inject (interpolate) patterns
 * Support pattern "text text {label:additional content} text {label} text";
 */
function parseInterpolationParts(text: string) {
	const parts: (string | InterpolationInjectPart)[] = [];

	let part;
	let lastPartEndIndex = 0;
	while ((part = interpolateRegExp.exec(text))) {
		const [{length}, label, , content] = part;
		const {index} = part;

		parts.push(text.substring(lastPartEndIndex, index), {label, content});

		lastPartEndIndex = index + length;
	}

	// Interpolation is not found so it seems is a plain string
	if (lastPartEndIndex === 0) {
		return null;
	}

	// Push plain part after last interpolation part
	parts.push(text.substr(lastPartEndIndex));

	return parts;
}

/**
 * Transforms strings tree to a flat object with dot-joined strings as key
 *  {addForm: {import: {title: 'Import from file'}}}
 *  Will be transformed to:
 *  {'addForm.import.title': 'Import from file'}
 *
 *  It also parse an interpolations pattern,
 *  like a "text {injectLabel} text" or "text {label:additional content} text"
 */
export function localesParser(translationsMap: TranslationsMap) {
	const map: TranslationsFlatMap = {};

	for (const [key, value] of Object.entries(translationsMap)) {
		if (typeof value === 'string') {
			const interpolationParts = parseInterpolationParts(value);
			if (interpolationParts) {
				map[key] = {type: 'interpolation', parts: interpolationParts};
			} else {
				map[key] = {type: 'plain', value};
			}
			continue;
		}

		const subResources = localesParser(value);
		for (const [subKey, subValue] of Object.entries(subResources)) {
			map[`${key}.${subKey}`] = subValue;
		}
	}

	return map;
}
